import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RotateCcw, Trophy, Lightbulb, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Sage green color palette
const SAGE_GREEN = {
  bg: '#7C9A82',
  light: '#A3C4A8',
  dark: '#5B7960',
};

const DIFFICULTY_OPTIONS = [
  { label: 'Easy', discs: 3, minMoves: 7 },
  { label: 'Medium', discs: 4, minMoves: 15 },
  { label: 'Hard', discs: 5, minMoves: 31 },
];

const SizeStack = () => {
  const navigate = useNavigate();
  const [difficulty, setDifficulty] = useState(DIFFICULTY_OPTIONS[0]);
  const [towers, setTowers] = useState([[], [], []]);
  const [selectedTower, setSelectedTower] = useState(null);
  const [moves, setMoves] = useState(0);
  const [isWon, setIsWon] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showDifficultyMenu, setShowDifficultyMenu] = useState(false);
  const errorTimeoutRef = useRef(null);

  useEffect(() => {
    initializeGame();
  }, [difficulty]);

  useEffect(() => {
    // Clear any pending error-message timeout on unmount so it can't
    // call setState after the component is gone.
    return () => {
      if (errorTimeoutRef.current) {
        clearTimeout(errorTimeoutRef.current);
        errorTimeoutRef.current = null;
      }
    };
  }, []);

  const initializeGame = () => {
    // Create discs based on difficulty
    // Array order: index 0 = bottom (largest), last index = top (smallest)
    const discs = [];
    for (let i = difficulty.discs; i >= 1; i--) {
      discs.push({
        id: `disc-${i}`,
        size: i,
      });
    }
    
    // All discs start on the leftmost tower (tower 0)
    // Largest at bottom (first in array), smallest on top (last in array)
    if (errorTimeoutRef.current) {
      clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = null;
    }
    setTowers([discs, [], []]);
    setMoves(0);
    setIsWon(false);
    setSelectedTower(null);
    setErrorMessage('');
  };

  const checkWin = (newTowers, currentMoves) => {
    // Win condition: All discs are on the rightmost tower (tower 2)
    return newTowers[2].length === difficulty.discs && currentMoves > 0;
  };

  const handleTowerClick = (towerIndex) => {
    if (isWon) return;
    setErrorMessage('');

    if (selectedTower === null) {
      // Select tower if it has discs
      if (towers[towerIndex].length > 0) {
        setSelectedTower(towerIndex);
      }
    } else {
      if (selectedTower === towerIndex) {
        // Deselect
        setSelectedTower(null);
      } else {
        // Try to move the top disc
        const sourceTower = [...towers[selectedTower]];
        const destTower = [...towers[towerIndex]];
        
        if (sourceTower.length > 0) {
          const disc = sourceTower[sourceTower.length - 1]; // Top disc (last in array)
          
          // Check if move is valid: can only place on empty tower or on larger disc
          const destTopDisc = destTower[destTower.length - 1];
          if (destTower.length === 0 || destTopDisc.size > disc.size) {
            // Valid move
            sourceTower.pop(); // Remove from source
            destTower.push(disc); // Add to top of destination
            
            const newTowers = [...towers];
            newTowers[selectedTower] = sourceTower;
            newTowers[towerIndex] = destTower;
            
            setTowers(newTowers);
            setMoves(m => m + 1);

            if (checkWin(newTowers, moves + 1)) {
              setIsWon(true);
            }
          } else {
            // Invalid move
            if (errorTimeoutRef.current) {
              clearTimeout(errorTimeoutRef.current);
            }
            setErrorMessage('Cannot place a larger disc on a smaller one!');
            errorTimeoutRef.current = setTimeout(() => {
              setErrorMessage('');
              errorTimeoutRef.current = null;
            }, 2000);
          }
        }
        setSelectedTower(null);
      }
    }
  };

  const getDiscWidth = (size) => {
    // Width scales with size
    const baseWidth = 30;
    const increment = 22;
    return baseWidth + size * increment;
  };

  const changeDifficulty = (newDifficulty) => {
    setDifficulty(newDifficulty);
    setShowDifficultyMenu(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E8F5E9] via-[#C8E6C9] to-[#A5D6A7] relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-green-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-teal-400 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 py-6">
        <header className="flex justify-between items-center mb-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-white text-[#2D3748] px-5 py-2 rounded-full font-semibold hover:bg-slate-100 transition-all duration-300 flex items-center gap-2 shadow-sm"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          
          <h1 className="text-2xl md:text-3xl font-bold text-[#2D3748]">Size Stack</h1>
          
          <button
            onClick={initializeGame}
            className="bg-white text-[#4A5568] px-5 py-2 rounded-full font-semibold hover:bg-slate-100 transition-all duration-300 flex items-center gap-2 shadow-sm"
          >
            <RotateCcw className="w-5 h-5" />
            Reset
          </button>
        </header>

        {/* Instructions */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 mb-4 text-center shadow-sm">
          <p className="text-[#4A5568]">
            <strong>Goal:</strong> Move all discs from the left tower to the right tower.
          </p>
          <p className="text-[#4A5568] text-sm mt-1">
            Rules: Move only one disc at a time • Can only place on a larger disc or empty tower
          </p>
        </div>

        {/* Error message */}
        <AnimatePresence>
          {errorMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-red-100 border border-red-300 text-red-700 rounded-xl px-4 py-2 mb-4 text-center"
            >
              {errorMessage}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Stats and difficulty */}
        <div className="flex justify-center gap-3 mb-4 flex-wrap">
          <div className="bg-white rounded-xl px-5 py-2 shadow-sm">
            <span className="text-[#4A5568]">Moves: </span>
            <span className="font-bold text-[#2D3748]">{moves}</span>
            <span className="text-[#9CA3AF] text-sm ml-2">(Min: {difficulty.minMoves})</span>
          </div>
          
          {/* Difficulty selector */}
          <div className="relative">
            <button
              onClick={() => setShowDifficultyMenu(!showDifficultyMenu)}
              className="bg-white rounded-xl px-5 py-2 shadow-sm flex items-center gap-2 hover:bg-slate-50 transition-colors"
            >
              <span className="text-[#4A5568]">Level:</span>
              <span className="font-bold text-[#2D3748]">{difficulty.label}</span>
              <ChevronDown className="w-4 h-4 text-[#4A5568]" />
            </button>
            
            <AnimatePresence>
              {showDifficultyMenu && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full mt-2 left-0 bg-white rounded-xl shadow-lg overflow-hidden z-20"
                >
                  {DIFFICULTY_OPTIONS.map((option) => (
                    <button
                      key={option.label}
                      onClick={() => changeDifficulty(option)}
                      className={`w-full px-5 py-2 text-left hover:bg-slate-100 transition-colors ${
                        difficulty.label === option.label ? 'bg-green-50 text-green-700' : 'text-[#4A5568]'
                      }`}
                    >
                      <span className="font-medium">{option.label}</span>
                      <span className="text-sm text-[#9CA3AF] ml-2">({option.discs} discs)</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button
            onClick={() => setShowHint(!showHint)}
            className="bg-amber-100 text-amber-700 rounded-xl px-5 py-2 shadow-sm flex items-center gap-2 hover:bg-amber-200 transition-colors"
          >
            <Lightbulb className="w-5 h-5" />
            Hint
          </button>
        </div>

        {/* Hint */}
        <AnimatePresence>
          {showHint && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-center"
            >
              <p className="text-amber-800 text-sm">
                💡 To move a stack of discs, first move the smaller discs to the middle tower, then move the largest disc to the right, and rebuild on top of it!
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Towers Container */}
        <div className="bg-white/50 backdrop-blur-sm rounded-2xl p-6 mb-4">
          <div className="flex justify-center items-end gap-6 md:gap-16">
            {towers.map((tower, towerIndex) => (
              <div
                key={towerIndex}
                onClick={() => handleTowerClick(towerIndex)}
                className={`relative cursor-pointer transition-all duration-300 ${
                  selectedTower === towerIndex ? 'scale-105' : 'hover:scale-102'
                }`}
                style={{ height: '220px', width: '160px' }}
              >
                {/* Goal indicator */}
                {towerIndex === 2 && (
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                    <span className="text-2xl">🎯</span>
                  </div>
                )}

                {/* Tower pole - positioned from the base */}
                <div 
                  className="absolute left-1/2 -translate-x-1/2 w-3 bg-gradient-to-b from-amber-500 to-amber-700 rounded-t-full"
                  style={{ bottom: '16px', height: '180px' }}
                ></div>
                
                {/* Tower base */}
                <div 
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 w-40 h-4 bg-gradient-to-r from-amber-600 to-amber-700 rounded-lg shadow-lg"
                ></div>
                
                {/* Discs - stack from bottom up, sitting ON the base */}
                <div 
                  className="absolute left-1/2 -translate-x-1/2 flex flex-col-reverse items-center"
                  style={{ bottom: '16px' }}
                >
                  {tower.map((disc, discIndex) => {
                    const isTopDisc = discIndex === tower.length - 1;
                    return (
                      <motion.div
                        key={disc.id}
                        initial={{ y: -20, opacity: 0 }}
                        animate={{ 
                          y: 0, 
                          opacity: 1,
                          scale: selectedTower === towerIndex && isTopDisc ? 1.05 : 1
                        }}
                        style={{ width: getDiscWidth(disc.size) }}
                        className={`h-7 rounded-lg shadow-md ${
                          selectedTower === towerIndex && isTopDisc
                            ? 'ring-4 ring-white ring-opacity-70' 
                            : ''
                        }`}
                      >
                        <div 
                          className="w-full h-full rounded-lg"
                          style={{ 
                            background: `linear-gradient(135deg, ${SAGE_GREEN.light} 0%, ${SAGE_GREEN.bg} 50%, ${SAGE_GREEN.dark} 100%)` 
                          }}
                        ></div>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Selection indicator */}
                {selectedTower === towerIndex && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-44 h-2 bg-green-500 rounded-full blur-sm"
                  ></motion.div>
                )}

                {/* Tower label */}
                <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap">
                  <span className="text-[#4A5568] font-medium text-sm">
                    {towerIndex === 0 ? 'Start' : towerIndex === 1 ? 'Middle' : 'Goal'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Progress indicator */}
        <div className="flex justify-center mt-8">
          <div className="bg-white rounded-xl px-6 py-3 shadow-sm">
            <span className="text-[#4A5568]">Progress: </span>
            <span className="font-bold text-green-600">{towers[2].length}/{difficulty.discs}</span>
            <span className="text-[#4A5568]"> discs on goal</span>
          </div>
        </div>

        {/* Win modal */}
        <AnimatePresence>
          {isWon && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-6"
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-yellow-300 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trophy className="w-10 h-10 text-yellow-900" />
                </div>
                <h2 className="text-3xl font-bold text-[#2D3748] mb-2">Congratulations!</h2>
                <p className="text-[#4A5568] mb-2">You moved all the discs!</p>
                <p className="text-2xl font-bold text-green-600 mb-2">
                  {moves} moves
                </p>
                {moves === difficulty.minMoves && (
                  <p className="text-amber-600 font-semibold mb-4">⭐ Perfect! Minimum moves achieved!</p>
                )}
                {moves > difficulty.minMoves && (
                  <p className="text-[#9CA3AF] text-sm mb-4">
                    Minimum possible: {difficulty.minMoves} moves
                  </p>
                )}
                <div className="flex gap-4 justify-center">
                  <button
                    onClick={initializeGame}
                    className="bg-slate-200 text-[#4A5568] px-6 py-3 rounded-full font-semibold hover:bg-slate-300 transition-colors"
                  >
                    Play Again
                  </button>
                  {difficulty.label !== 'Hard' && (
                    <button
                      onClick={() => {
                        const nextIdx = DIFFICULTY_OPTIONS.findIndex(d => d.label === difficulty.label) + 1;
                        if (nextIdx < DIFFICULTY_OPTIONS.length) {
                          changeDifficulty(DIFFICULTY_OPTIONS[nextIdx]);
                        }
                      }}
                      className="bg-gradient-to-r from-green-500 to-teal-500 text-white px-6 py-3 rounded-full font-bold hover:-translate-y-1 transition-all duration-300 shadow-lg"
                    >
                      Try Harder
                    </button>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SizeStack;
